import React, { useEffect, useState } from "react";
import { Card, CardBody, Label } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faDownload,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import DataTable, { TableColumn } from "react-data-table-component";
import ButtonComponentV2 from "../../../components/ButtonComponentV2";
import BreadCrumbs from "../../../components/BreadCrumbs";
import {
  Payout,
  useLazyGetPayoutListQuery,
  useSettlePayoutRequestMutation,
} from "../../../redux/services/payOutService";
import { Button, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import TableLoadingComponent from "../../../components/TableLoadingComponent";
import * as XLSX from "xlsx";
import StatusBadge from "../../../components/StatusBatch";
import Swal from "sweetalert2";
import ViewButtonComponent from "../../../components/ViewButtonComponent";
import Select from "react-select";
import './index.css';
import { useDispatch, useSelector } from "react-redux";
import { setRowsPerPage } from "../../../redux/payoutSlice";
import { PAYMENT_BREAKDOWN_SORT_TYPE } from "../../../constants/constans";
import { toast } from "react-toastify";

const Payouts: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const rowsPerPage = useSelector((state: any) => state.payouts.rowsPerPage);
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [tableData, setTableData] = useState<Payout[]>([]);
  const [settelingPayoutId, setSettelingPayoutId] = useState<string | null>(null);

  const handleViewPayoutDetails = (row: Payout) => {
    navigate("/payment/payout/view", {
      state: { payoutDetails: row },
      replace: false,
    });
  };

  // RTK mutation hook
  const [
    settlePayoutRequest,
  ] = useSettlePayoutRequestMutation();

  const [trigger, { data, isLoading }] = useLazyGetPayoutListQuery();

  useEffect(() => {
    trigger(selectedStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  useEffect(() => {
    if (data) {
      setTableData([...data.restaurant, ...data.cart]);
    }
  }, [data]);

  const hadaleQuickSettle = async (requestId: string): Promise<void> => {
    const obj: {
      payoutRequestId: string;
      status: "Completed" | "Cancelled";
    } = {
      payoutRequestId: requestId,
      status: "Completed",
    };

    Swal.fire({
      title: "Do you want to mark this payout as settled?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#9F9F9F",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        try {
          setSettelingPayoutId(requestId);
          await settlePayoutRequest(obj).unwrap();
          toast.success("Payout settled successfully");
          setSettelingPayoutId(null);
          trigger(selectedStatus);
        } catch (error) {
          console.error("Error occurred while updating payout status:", error);
          toast.error("Failed to settle payout. Please try again.");
        } finally {
          setSettelingPayoutId(null);
        }
      }
    });
  };

  const columns: TableColumn<Payout>[] = [
    {
      name: <h6 className="fw-bold">Requested Date</h6>,
      cell: (row) => {
        const date = new Date(row.createdAt._seconds * 1000);
        const formattedDate = date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        return (
          <div
            className="d-flex gap-2"
            style={{
              fontSize: "14px",
            }}
          >
            {formattedDate}
          </div>
        );
      },
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Requested Type</h6>,
      cell: (row) => (
        <div
          className="d-flex gap-2"
          style={{
            fontSize: "14px",
          }}
        >
          {row.requesterType}
        </div>
      ),
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Cart Name</h6>,
      cell: (row) => (
        <div
          className="d-flex gap-2"
          style={{
            fontSize: "14px",
          }}
        >
          {row.cart?.displayName}
        </div>
      ),
      sortable: false,
    },

    {
      name: <h6 className="fw-bold">Amount</h6>,
      cell: (row) => (
        <div
          className="d-flex gap-2"
          style={{
            fontSize: "14px",
          }}
        >
          {row.amount}
        </div>
      ),
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Status</h6>,
      cell: (row) => <StatusBadge status={row.status} />,
      sortable: false,
    },
    {
      name: <h6 className="fw-bold">Action</h6>,
      cell: (row) => {
        if (row.status === "Pending") {
          return (
            <div className="d-flex gap-2">
              <ViewButtonComponent onClick={() => handleViewPayoutDetails(row)} />
              &nbsp; &nbsp;
              {settelingPayoutId === row.id ? (
                <Button
                  variant="outline-info"
                  style={{
                    border: "2px solid #198754",
                    color: "#198754",
                    backgroundColor: "transparent",
                  }}
                  disabled={true}
                >
                  <FontAwesomeIcon
                    icon={faSpinner}
                    title={"Loading"}
                    style={{ height: "15px" }}
                    spin
                  />
                </Button>
              ) : (
                <Button
                  onClick={() => hadaleQuickSettle(row.id)}
                  variant="outline-info"
                  style={{
                    border: "2px solid #198754",
                    color: "#198754",
                    backgroundColor: "transparent",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCheck}
                    title={"Settle Payment"}
                    style={{ height: "15px" }}
                  />
                </Button>
              )}
            </div>
          );
        }
        if (row.status === "Completed") {
          return (
            <div className="d-flex gap-2">
              <ViewButtonComponent
                onClick={() => handleViewPayoutDetails(row)} />
            </div>
          );
        }
        if (row.status === "Cancelled") {
          return (
            <div className="d-flex gap-2">
              <ViewButtonComponent
                onClick={() => handleViewPayoutDetails(row)} />
            </div>
          );
        }
        return null;
      },
    },
  ];

  const generateFileName = () => {
    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const formattedTime = now
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();
    return `Payouts_${formattedDate}, ${formattedTime}.csv`;
  };

  const downloadAsCsvFile = () => {
    if (!data) return;

    // Define the header
    const csvHeader = [
      "Requested Date",
      "Requested Type",
      "Cart Name",
      "Amount",
      "Status",
      "Bank Account Number",
      "Bank Name",
      "Branch",
      "last Updated Date",
    ];

    // Map data to rows
    const content = tableData.map((payout: any) => {
      const requestedDate = new Date(payout.createdAt._seconds * 1000)
        .toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        .replace(/,/g, "-")
        .replace(/\//g, " ");

      const requesterType =
        payout.requesterType === "Admin"
          ? "Admin"
          : payout.requesterType === "CartPerson"
            ? "CartPerson"
            : "N/A";

      const cartName = payout.cart?.displayName || "N/A";
      const amount = payout?.amount || 0;
      const status = payout?.status || "N/A";
      const accountNumber = payout?.userBankDetails?.accountNumber || "N/A";
      const bankName = payout?.userBankDetails?.bankName || "N/A";
      const branchName = payout?.userBankDetails?.branchName || "N/A";
      const updateDate = new Date(
        payout?.userBankDetails?.updatedAt?._seconds * 1000
      )
        .toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        .replace(/,/g, "-")
        .replace(/\//g, " ");

      return [
        requestedDate,
        requesterType,
        cartName,
        amount,
        status,
        accountNumber,
        bankName,
        branchName,
        updateDate,
      ];
    });

    // Combine header and rows
    const worksheetData = [csvHeader, ...content];

    // Create a worksheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Define the range for the header
    const headerRange = XLSX.utils.decode_range(worksheet["!ref"] || "A1:E1");

    for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!worksheet[cellAddress]) continue;
      if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
      worksheet[cellAddress].s = {
        font: {
          bold: true,
        },
      };
    }

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Payouts");

    // Generate Excel file and trigger download
    const fileName = generateFileName().replace(/\.csv$/i, ".xlsx");
    XLSX.writeFile(workbook, fileName);
  };

  const handleStatusChange = (selectedOption: { value: string; label: string } | null) => {
    const newStatus = selectedOption ? selectedOption.value : "All";
    setSelectedStatus(newStatus);
    trigger(newStatus);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    dispatch(setRowsPerPage(newRowsPerPage));
  };

  return (
    <>
      <BreadCrumbs hasSubMenus={true} />
      <Row className='mt-3 d-flex justify-content-between'>
        <Col lg="4">
          <h4>Payouts</h4>
        </Col>
        <Col lg="2">
        </Col>
        <Col lg="6" className="d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2 align-items-start justify-content-left">
            <div className="d-flex flex-column align-items-start">
              <Label>Filtered by</Label>
              <Select
                options={PAYMENT_BREAKDOWN_SORT_TYPE}
                onChange={handleStatusChange}
                value={PAYMENT_BREAKDOWN_SORT_TYPE.find(option => option.value === selectedStatus)}
                className="fixed-width-select w-100"
              />
            </div>
            <ButtonComponentV2
              titel="Download"
              icon={
                <FontAwesomeIcon
                  icon={faDownload}
                  title={"Download CSV File"}
                />
              }
              onClick={downloadAsCsvFile}
              color="#fff"
              backgroundColor="#7a1c37"
            />
          </div>
        </Col>
      </Row>

      {/* {isSettleLoading && (
        <div className="loading-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )} */}

      <br />
      <Card className="cart-view-whole-card">
        <CardBody>
          <div>
            {isLoading ? (
              <TableLoadingComponent numColumns={4} />
            ) : (
              <DataTable
                columns={columns}
                data={tableData}
                responsive
                pagination
                highlightOnHover
                pointerOnHover
                noDataComponent={<div className="p-5">No Payouts found</div>}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                paginationPerPage={rowsPerPage}
              />
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default Payouts;
